import { Label } from '@workspace/ui/components/label';
import {
  Toggle as TogglePrimitive,
  ToggleTabsList as ToggleTabsListPrimitive,
  ToggleTabs as ToggleTabsPrimitive,
  ToggleTabsTrigger as ToggleTabsTriggerPrimitive,
} from '@workspace/ui/components/toggle';
import { cn } from '@workspace/ui/lib';

interface ToggleProps {
  label?: string;
  description?: string;
  disabled?: boolean;
  value?: boolean;
  defaultValue?: boolean;
  onChange?: (value: boolean) => void;
  className?: string;
  id?: string;
  Icon?: React.ComponentType<{ className?: string }>;
  iconClassName?: string;
  box?: boolean;
  labelClassName?: string;
}
export const Toggle = ({
  label,
  description,
  disabled,
  value,
  defaultValue,
  onChange,
  className,
  id,
  Icon,
  iconClassName,
  box,
  labelClassName,
}: ToggleProps) => {
  return (
    <div
      className={cn(
        'flex justify-between gap-3',
        box
          ? 'border-grey-400 dark:bg-grey-1000 rounded-2xl border p-6 dark:border-transparent'
          : '',
        description ? 'items-start' : 'items-center',
        className
      )}
    >
      <div className={description ? 'flex flex-col gap-2' : ''}>
        <div className="flex items-center gap-2">
          {Icon && <Icon className={iconClassName || 'h-5 w-5 text-pink-400 dark:text-pink-400'} />}
          <Label
            className={cn(
              'text-sm font-medium text-neutral-200 dark:text-neutral-100',
              labelClassName
            )}
            htmlFor={id}
          >
            {label}
          </Label>
        </div>
        {description && <p className="text-grey-600 dark:text-grey-500 text-sm">{description}</p>}
      </div>
      <TogglePrimitive
        onCheckedChange={onChange}
        checked={value}
        defaultChecked={defaultValue}
        disabled={disabled}
        className={className}
        id={id}
      />
    </div>
  );
};

export interface ToggleTabsSettings {
  label?: string;
  disabled?: boolean;
  value: string;
  onValueChange?: (value: string) => void;
  icon?: React.ComponentType<{ className?: string }>;
}
interface ToggleTabsProps {
  value: string;
  className?: string;
  settings: ToggleTabsSettings[];
  onValueChange?: (value: string) => void;
  hideInactiveLabels?: boolean;
}

export const ToggleTabs = ({
  value,
  settings,
  className,
  onValueChange,
  hideInactiveLabels = false,
}: ToggleTabsProps) => {
  return (
    <ToggleTabsPrimitive value={value} className={className} onValueChange={onValueChange}>
      <ToggleTabsListPrimitive hideInactiveLabels={hideInactiveLabels}>
        {settings.map((setting) => {
          const Icon = setting.icon;
          const isActive = value === setting.value;

          return (
            <ToggleTabsTriggerPrimitive
              key={setting.value}
              value={setting.value}
              disabled={setting.disabled}
              className={cn(
                'flex items-center gap-2',
                hideInactiveLabels && !isActive && 'justify-center'
              )}
            >
              {Icon && <Icon className="h-4 w-4" />}
              {(!hideInactiveLabels || isActive) && setting.label}
            </ToggleTabsTriggerPrimitive>
          );
        })}
      </ToggleTabsListPrimitive>
    </ToggleTabsPrimitive>
  );
};
